/**
 * Zaman çekirdeği.
 *
 * Prototipteki hata (algorithm.ts:118):
 *     new Date(`${sessionDate}T${sessionTime}:00`)
 * Bu ifade SUNUCUNUN yerel saatini kullanır. Sunucu UTC'de (Cloud Run varsayılanı),
 * stüdyo İstanbul'da olduğu için 24 saatlik iptal kuralı 3 saat kayık çalışıyordu:
 * derse 22 saat kalmış bir üye "iade alabilirsiniz" cevabı alıyordu.
 *
 * Kural: veritabanında her şey UTC `Date`. Duvar saati ("09:00") yalnızca
 * vardiya tanımında ve ekranda yaşar; ikisi arasındaki dönüşüm SADECE burada olur.
 */

export const MINUTES_PER_HOUR = 60;
export const MS_PER_MINUTE = 60_000;
export const MS_PER_HOUR = 3_600_000;

/** "09:30" → 570. Geçersiz girdide fırlatır — sessizce NaN döndürmez. */
export function timeToMinutes(timeStr: string): number {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeStr);
  if (!match) {
    throw new RangeError(`Geçersiz saat formatı: "${timeStr}" (beklenen HH:MM)`);
  }
  return Number(match[1]) * MINUTES_PER_HOUR + Number(match[2]);
}

/** 570 → "09:30". Gün sınırını aşan değerlerde fırlatır. */
export function minutesToTime(totalMinutes: number): string {
  if (!Number.isInteger(totalMinutes) || totalMinutes < 0 || totalMinutes >= 1440) {
    throw new RangeError(`Dakika değeri 0-1439 aralığında olmalıdır: ${totalMinutes}`);
  }
  const h = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const m = totalMinutes % MINUTES_PER_HOUR;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * İki yarı açık aralığın [startA, endA) ve [startB, endB) çakışıp çakışmadığı.
 * Prototipteki matematik doğruydu; korunuyor, yalnızca sayısal tipe taşındı
 * (string parse maliyeti çağrı başına 4 kez ödenmiyor).
 *
 * 09:00-09:50 ile 09:50-10:40 ÇAKIŞMAZ (bitişik).
 */
export function hasOverlap(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && endA > startB;
}

/** Date nesneleri için aynı kontrol. */
export function hasDateOverlap(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
  return startA.getTime() < endB.getTime() && endA.getTime() > startB.getTime();
}

/**
 * Verilen anda, verilen zaman diliminin UTC'ye göre ofseti (ms).
 * Intl tabanlı — IANA veritabanını kullanır, DST kurallarını doğru bilir.
 */
export function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts: Record<string, number> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }

  const asUtc = Date.UTC(
    parts.year ?? 1970,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    (parts.hour ?? 0) % 24, // bazı ICU sürümleri gece yarısını "24" olarak verir
    parts.minute ?? 0,
    parts.second ?? 0,
  );

  return asUtc - instant.getTime();
}

/**
 * Stüdyo duvar saatini ("2026-08-05", "09:00") gerçek bir UTC anına çevirir.
 *
 * DST geçişlerinde ofset, sonucun kendisine bağlıdır (tavuk-yumurta). Bu yüzden
 * bir tahminle başlayıp bir kez düzeltiyoruz — standart ve yeterli yöntem.
 */
export function zonedWallTimeToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!dateMatch) throw new RangeError(`Geçersiz tarih: "${dateStr}" (beklenen YYYY-MM-DD)`);

  const minutes = timeToMinutes(timeStr);
  const naiveUtc = Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3]),
    Math.floor(minutes / MINUTES_PER_HOUR),
    minutes % MINUTES_PER_HOUR,
  );

  const firstGuess = timeZoneOffsetMs(new Date(naiveUtc), timeZone);
  const candidate = naiveUtc - firstGuess;

  const refined = timeZoneOffsetMs(new Date(candidate), timeZone);
  return new Date(refined === firstGuess ? candidate : naiveUtc - refined);
}

/** UTC anını stüdyo saat dilimindeki takvim gününe çevirir: "2026-08-05". */
export function utcToZonedDateString(instant: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(instant); // en-CA → YYYY-MM-DD
}

/** UTC anını stüdyo saat dilimindeki duvar saatine çevirir: "09:00". */
export function utcToZonedTimeString(instant: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
  return dtf.format(instant).replace('24:', '00:');
}

/** Stüdyo saat dilimine göre haftanın günü (0 = Pazar). */
export function zonedDayOfWeek(dateStr: string, timeZone: string): number {
  const noonUtc = zonedWallTimeToUtc(dateStr, '12:00', timeZone);
  const dtf = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' });
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return names.indexOf(dtf.format(noonUtc));
}

/** Stüdyo gününün UTC olarak [başlangıç, bitiş) sınırları. */
export function zonedDayBounds(dateStr: string, timeZone: string): { start: Date; end: Date } {
  const start = zonedWallTimeToUtc(dateStr, '00:00', timeZone);
  const end = new Date(start.getTime() + 24 * MS_PER_HOUR);
  return { start, end };
}

/**
 * "YYYY-MM-DD" aralığını gün gün listeler (her iki uç dahil).
 *
 * Takvim günü aritmetiği UTC öğlen üzerinden yapılır: gün başında (00:00)
 * ilerlemek, DST uygulayan zonlarda saatin bir gün atlamasına veya
 * tekrarlamasına yol açabilir. Öğlen bu riskten uzaktır.
 */
export function enumerateDates(fromDateStr: string, toDateStr: string): string[] {
  const from = Date.parse(`${fromDateStr}T12:00:00Z`);
  const to = Date.parse(`${toDateStr}T12:00:00Z`);

  if (Number.isNaN(from) || Number.isNaN(to)) {
    throw new RangeError(`Geçersiz tarih aralığı: ${fromDateStr} → ${toDateStr}`);
  }
  if (to < from) return [];

  const dates: string[] = [];
  for (let cursor = from; cursor <= to; cursor += 24 * MS_PER_HOUR) {
    dates.push(new Date(cursor).toISOString().slice(0, 10));
  }
  return dates;
}

/** İki takvim günü arasındaki tam gün farkı. */
export function daysBetweenDates(fromDateStr: string, toDateStr: string): number {
  const from = Date.parse(`${fromDateStr}T12:00:00Z`);
  const to = Date.parse(`${toDateStr}T12:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) {
    throw new RangeError(`Geçersiz tarih aralığı: ${fromDateStr} → ${toDateStr}`);
  }
  return Math.round((to - from) / (24 * MS_PER_HOUR));
}

/** Takvim gününe gün ekler ("2026-08-31" + 1 → "2026-09-01"); UTC öğlen üzerinden, DST'ye bağışık. */
export function addDaysToDate(dateStr: string, days: number): string {
  const base = Date.parse(dateStr + 'T12:00:00Z');
  if (Number.isNaN(base)) throw new RangeError('Geçersiz tarih: ' + dateStr);
  return new Date(base + days * 24 * MS_PER_HOUR).toISOString().slice(0, 10);
}

/** Verilen günün içinde bulunduğu haftanın Pazartesi'si. */
export function startOfWeek(dateStr: string, timeZone: string): string {
  const dayOfWeek = zonedDayOfWeek(dateStr, timeZone);
  const offsetToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Pazar → önceki Pazartesi
  const shifted = Date.parse(`${dateStr}T12:00:00Z`) + offsetToMonday * 24 * MS_PER_HOUR;
  return new Date(shifted).toISOString().slice(0, 10);
}

/**
 * İki an arasındaki KESİN saat farkı. Yuvarlama YOK.
 *
 * Politika kararları (24 saat iptal penceresi gibi) MUTLAKA bunu kullanmalıdır.
 * Yuvarlanmış bir değerle karşılaştırma yapmak kuralı deler: 23sa 59dk fark
 * bir ondalığa yuvarlandığında 24.0 olur ve `>= 24` kontrolünü geçer —
 * yani üye derse 1 dakika kala iptal edip kredisini geri alır.
 */
export function hoursBetween(from: Date, to: Date): number {
  return (to.getTime() - from.getTime()) / MS_PER_HOUR;
}

/** Yalnızca EKRANDA gösterim için. Karşılaştırmada kullanma. */
export function hoursBetweenForDisplay(from: Date, to: Date): number {
  return Math.round(hoursBetween(from, to) * 10) / 10;
}

/**
 * Test edilebilirlik için saat enjeksiyonu.
 * `new Date()` doğrudan çağrılırsa iptal penceresi testleri deterministik olmaz.
 */
export interface Clock {
  now(): Date;
}

export const systemClock: Clock = { now: () => new Date() };

export function fixedClock(instant: Date | string): Clock {
  const frozen = typeof instant === 'string' ? new Date(instant) : instant;
  return { now: () => new Date(frozen) };
}
